import { useState, useMemo, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid,
  ComposedChart, Bar, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ReferenceLine
} from 'recharts';
import { Printer, Target, Rocket, BarChart3, Edit3, Check, Sparkles } from 'lucide-react';

const MONTHS_SHORT = ['Jan', 'Fev', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aou', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_FULL = ['Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'];
const CHART_TYPES = [{ id: 'area', label: 'Gradient' }, { id: 'bars', label: 'Barres' }, { id: 'radar', label: 'Radar' }];

const CustomTooltip = ({ active, payload, label, fmt }) => {
  if (!active || !payload) return null;
  return (
    <div className="bg-white/95 backdrop-blur-sm border border-slate-200 rounded-xl shadow-xl p-3 min-w-[180px]">
      <p className="text-xs font-bold text-slate-800 mb-2">{label}</p>
      {payload.filter(p => p.value != null).map((p, i) => (
        <div key={i} className="flex justify-between items-center gap-4 py-0.5">
          <span className="flex items-center gap-1.5 text-xs text-slate-600">
            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: p.color }} />
            {p.name}
          </span>
          <span className="text-xs font-semibold text-slate-900">{fmt(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

const buildMonthRange = (startYear, startMonth, endYear, endMonth) => {
  const months = [];
  let y = startYear, m = startMonth;
  while (y < endYear || (y === endYear && m <= endMonth)) {
    months.push({ year: y, month: m, label: `${MONTHS_SHORT[m]} ${y}`, labelFull: `${MONTHS_FULL[m]} ${y}` });
    m++;
    if (m > 11) { m = 0; y++; }
  }
  return months;
};

const ProjectionsPage = () => {
  const [title, setTitle] = useState('Projection financiere');
  const [description, setDescription] = useState('');
  const [currency, setCurrency] = useState('CHF');
  const [editingTitle, setEditingTitle] = useState(false);
  const [chartType, setChartType] = useState('area');

  const now = new Date();
  const [startYear, setStartYear] = useState(now.getFullYear());
  const [startMonth, setStartMonth] = useState(now.getMonth());
  const [endYear, setEndYear] = useState(now.getFullYear() + 1);
  const [endMonth, setEndMonth] = useState(now.getMonth() === 0 ? 11 : now.getMonth() - 1);

  // Situation actuelle: manual monthly values + annual target
  const [realValues, setRealValues] = useState({});
  const [realTarget, setRealTarget] = useState(0);
  const [realNote, setRealNote] = useState('');

  // Objectif realiste + developpement: annual target + note
  const [realisticTarget, setRealisticTarget] = useState(0);
  const [realisticNote, setRealisticNote] = useState('');
  const [growthTarget, setGrowthTarget] = useState(0);
  const [growthNote, setGrowthNote] = useState('');

  const fmt = (n) => new Intl.NumberFormat('fr-CH', { minimumFractionDigits: 0 }).format(Math.round(n || 0)) + ' ' + currency;
  const monthRange = useMemo(() => buildMonthRange(startYear, startMonth, endYear, endMonth), [startYear, startMonth, endYear, endMonth]);
  const periodMonths = monthRange.length;
  const yearOptions = Array.from({ length: 6 }, (_, i) => now.getFullYear() - 2 + i);

  // Compute trend for "Situation actuelle"
  const currentData = useMemo(() => {
    const filled = [];
    const projected = [];
    let lastFilledIdx = -1;

    // Find which months have real values
    monthRange.forEach((m, i) => {
      const key = `${m.year}-${m.month}`;
      const val = realValues[key];
      if (val !== undefined && val !== '' && val !== null) {
        filled.push({ idx: i, val: parseFloat(val) || 0 });
        lastFilledIdx = i;
      }
    });

    const result = monthRange.map(() => null);

    if (filled.length === 0) {
      // No data — use target / months as flat line
      const monthly = realTarget > 0 ? realTarget / periodMonths : 0;
      monthRange.forEach((_, i) => { result[i] = Math.round(monthly); });
      return { values: result, lastRealIdx: -1 };
    }

    // Fill real values
    filled.forEach(f => { result[f.idx] = f.val; });

    // Project remaining months based on average of real + target
    if (lastFilledIdx < periodMonths - 1) {
      const realSum = filled.reduce((s, f) => s + f.val, 0);
      const remaining = periodMonths - lastFilledIdx - 1;
      const targetRemaining = Math.max(0, realTarget - realSum);
      const monthlyProjected = remaining > 0 ? targetRemaining / remaining : 0;

      // If no target, continue with average of filled
      const avgFilled = filled.length > 0 ? realSum / filled.length : 0;
      const projVal = realTarget > 0 ? monthlyProjected : avgFilled;

      for (let i = lastFilledIdx + 1; i < periodMonths; i++) {
        result[i] = Math.round(projVal);
      }
    }

    return { values: result, lastRealIdx: lastFilledIdx };
  }, [realValues, realTarget, monthRange, periodMonths]);

  // Compute trends for realistic and growth — total must equal annualTarget * (periodMonths/12)
  const computeObjective = (annualTarget) => {
    if (periodMonths === 0 || annualTarget === 0) return monthRange.map(() => 0);
    const periodTarget = Math.round(annualTarget * (periodMonths / 12));
    const monthlyTarget = annualTarget / 12;

    // Start from current real average
    const filledVals = Object.values(realValues).filter(v => v !== '' && v !== null && v !== undefined).map(v => parseFloat(v) || 0);
    const currentAvg = filledVals.length > 0 ? filledVals.reduce((s, v) => s + v, 0) / filledVals.length : monthlyTarget * 0.7;

    // Create progressive shape (start lower, end higher)
    // but ensure the sum equals periodTarget exactly
    const rawWeights = monthRange.map((_, i) => {
      if (periodMonths <= 1) return 1;
      return currentAvg + (monthlyTarget - currentAvg) * (i / (periodMonths - 1));
    });
    const rawSum = rawWeights.reduce((s, v) => s + v, 0);
    const scale = rawSum > 0 ? periodTarget / rawSum : 1;

    return rawWeights.map(w => Math.round(w * scale));
  };

  const realisticData = useMemo(() => computeObjective(realisticTarget), [realisticTarget, realValues, periodMonths]);
  const growthData = useMemo(() => computeObjective(growthTarget), [growthTarget, realValues, periodMonths]);

  const totalCurrent = currentData.values.reduce((s, v) => s + (v || 0), 0);
  const realTotal = useMemo(() => {
    return Object.entries(realValues).reduce((s, [, v]) => s + (parseFloat(v) || 0), 0);
  }, [realValues]);
  const totalRealistic = realisticData.reduce((s, v) => s + v, 0);
  const totalGrowth = growthData.reduce((s, v) => s + v, 0);

  const chartData = monthRange.map((m, i) => ({
    month: m.label,
    'Scenario prudent': currentData.values[i],
    'Scenario realiste': realisticTarget > 0 ? realisticData[i] : null,
    'Scenario de developpement': growthTarget > 0 ? growthData[i] : null,
  }));

  const scenarios = [
    { label: 'Scenario prudent', color: '#3b82f6', total: totalCurrent, target: realTarget, note: realNote, monthly: realTarget > 0 ? realTarget / 12 : totalCurrent / Math.max(1, periodMonths) },
    { label: 'Scenario realiste', color: '#f59e0b', total: totalRealistic, target: realisticTarget, note: realisticNote, monthly: realisticTarget / 12 },
    { label: 'Scenario de developpement', color: '#10b981', total: totalGrowth, target: growthTarget, note: growthNote, monthly: growthTarget / 12 },
  ];

  const chartRef = useRef(null);

  const handlePrint = () => {
    // Capture the main Recharts chart SVG (direct child of wrapper, not legend icons)
    let chartSvgHtml = '';
    if (chartRef.current) {
      const svgEl = chartRef.current.querySelector('.recharts-wrapper > .recharts-surface');
      if (svgEl) {
        const clone = svgEl.cloneNode(true);
        const wrapper = chartRef.current.querySelector('.recharts-wrapper');
        const w = wrapper ? wrapper.offsetWidth : 800;
        const h = wrapper ? wrapper.offsetHeight : 360;
        clone.setAttribute('width', w);
        clone.setAttribute('height', h);
        clone.setAttribute('viewBox', `0 0 ${w} ${h}`);
        clone.style.maxWidth = '100%';
        chartSvgHtml = clone.outerHTML;
      }
    }

    const periodLabel = `${monthRange[0]?.labelFull} a ${monthRange[monthRange.length - 1]?.labelFull}`;
    const win = window.open('', '_blank');
    win.document.write(`<html><head><title>${title}</title>
<style>
*{margin:0;padding:0;box-sizing:border-box}
body{font-family:'Helvetica Neue',Arial,sans-serif;color:#1e293b;padding:30px;line-height:1.5}
h1{font-size:26px;color:#0f172a;margin-bottom:2px;font-weight:800}
h2{font-size:15px;color:#1e40af;margin:28px 0 10px;font-weight:700;border-bottom:2px solid #dbeafe;padding-bottom:4px}
.desc{color:#475569;font-size:12px;margin-bottom:16px;line-height:1.6}
.period{background:#eff6ff;border:1px solid #bfdbfe;border-radius:8px;padding:10px 16px;font-size:12px;color:#1e40af;margin-bottom:20px;font-weight:500}
.cards{display:grid;grid-template-columns:1fr 1fr 1fr;gap:14px;margin-bottom:24px}
.card{border-radius:10px;padding:18px;position:relative;overflow:hidden;page-break-inside:avoid}
.card::before{content:'';position:absolute;top:0;left:0;right:0;height:3px}
.c0{background:#eff6ff;border:1px solid #bfdbfe}.c0::before{background:#3b82f6}
.c1{background:#fffbeb;border:1px solid #fde68a}.c1::before{background:#f59e0b}
.c2{background:#ecfdf5;border:1px solid #a7f3d0}.c2::before{background:#10b981}
.card .lbl{font-size:10px;color:#64748b;text-transform:uppercase;letter-spacing:.8px;font-weight:600;margin-bottom:6px}
.card .amt{font-size:22px;font-weight:800;margin-bottom:4px}
.card .det{font-size:11px;color:#64748b;margin-bottom:2px}
.obs{margin-top:10px;padding-top:8px;border-top:1px solid rgba(0,0,0,.08);font-size:11px;color:#334155;line-height:1.7;white-space:pre-wrap}
.obs-t{font-size:9px;color:#94a3b8;text-transform:uppercase;letter-spacing:.5px;margin-bottom:3px}
.chart-container{border:1px solid #e2e8f0;border-radius:10px;padding:16px;margin-bottom:24px;page-break-inside:avoid}
.chart-title{font-size:13px;font-weight:700;color:#1e40af;margin-bottom:10px}
table{width:100%;border-collapse:collapse;font-size:10px;margin-bottom:16px}
th{background:linear-gradient(135deg,#1e40af,#3b82f6);color:white;padding:7px 8px;text-align:right;font-weight:600}
th:first-child{text-align:left;border-radius:6px 0 0 0}th:last-child{border-radius:0 6px 0 0}
td{padding:5px 8px;border-bottom:1px solid #e2e8f0;text-align:right}
td:first-child{text-align:left;font-weight:500}
tr:nth-child(even){background:#f8fafc}
.real{font-weight:700}.proj{font-style:italic;color:#94a3b8}
.total td{font-weight:700;border-top:2px solid #1e40af;background:#eff6ff}
.obs-section{page-break-inside:avoid}
.obs-block{margin-bottom:14px;padding:12px 16px;background:#f8fafc;border-radius:0 8px 8px 0;page-break-inside:avoid}
@media print{body{padding:15px} .chart-container{page-break-inside:avoid} .obs-section{page-break-before:auto} .obs-block{page-break-inside:avoid}}
</style></head><body>
<h1>${title}</h1>
${description ? `<p class="desc">${description}</p>` : ''}
<div class="period">Periode : ${periodLabel} (${periodMonths} mois)</div>
<div class="cards">
${scenarios.map((s, i) => `<div class="card c${i}">
<p class="lbl">${s.label}</p>
<p class="amt" style="color:${s.color}">${fmt(s.total)}</p>
<p class="det">Objectif annuel : ${fmt(s.target)}</p>
<p class="det">~${fmt(s.monthly)}/mois</p>
${s.note ? `<div class="obs"><p class="obs-t">Observation</p>${s.note.replace(/\n/g, '<br>')}</div>` : ''}
</div>`).join('')}
</div>
${chartSvgHtml ? `<div class="chart-container"><p class="chart-title">Projection sur ${periodMonths} mois</p>${chartSvgHtml}</div>` : ''}
<h2>Detail mensuel</h2>
<table>
<tr><th>Mois</th><th>Scenario prudent</th><th>Scenario realiste</th><th>Scenario de developpement</th></tr>
${monthRange.map((m, i) => {
  const isReal = i <= currentData.lastRealIdx;
  return `<tr>
<td>${m.label}</td>
<td class="${isReal ? 'real' : 'proj'}">${fmt(currentData.values[i])}${isReal ? '' : ' *'}</td>
<td>${realisticTarget > 0 ? fmt(realisticData[i]) : '-'}</td>
<td>${growthTarget > 0 ? fmt(growthData[i]) : '-'}</td>
</tr>`;
}).join('')}
<tr class="total"><td>Total</td><td>${fmt(totalCurrent)}</td><td>${fmt(totalRealistic)}</td><td>${fmt(totalGrowth)}</td></tr>
</table>
<p style="font-size:9px;color:#94a3b8;margin-top:-10px">* Valeurs projetees (tendance)</p>
${scenarios.some(s => s.note) ? `<div class="obs-section"><h2>Observations & Hypotheses</h2>
${scenarios.filter(s => s.note).map(s => `<div class="obs-block" style="border-left:3px solid ${s.color}">
<p style="font-size:11px;font-weight:700;color:${s.color};margin-bottom:4px">${s.label}</p>
<p style="font-size:11px;color:#334155;line-height:1.7;white-space:pre-wrap">${s.note.replace(/\n/g, '<br>')}</p>
</div>`).join('')}</div>` : ''}
</body></html>`);
    win.document.close();
    setTimeout(() => win.print(), 500);
  };

  const renderChart = () => {
    const h = 360;
    const scList = [
      { key: 'Scenario prudent', color: '#3b82f6' },
      ...(realisticTarget > 0 ? [{ key: 'Scenario realiste', color: '#f59e0b' }] : []),
      ...(growthTarget > 0 ? [{ key: 'Scenario de developpement', color: '#10b981' }] : []),
    ];
    if (chartType === 'area') {
      return (
        <ResponsiveContainer width="100%" height={h}>
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="g0" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#3b82f6" stopOpacity={0.3}/><stop offset="100%" stopColor="#3b82f6" stopOpacity={0}/></linearGradient>
              <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#f59e0b" stopOpacity={0.25}/><stop offset="100%" stopColor="#f59e0b" stopOpacity={0}/></linearGradient>
              <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10b981" stopOpacity={0.25}/><stop offset="100%" stopColor="#10b981" stopOpacity={0}/></linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#64748b' }} angle={-30} textAnchor="end" height={50} />
            <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
            <Tooltip content={<CustomTooltip fmt={fmt} />} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {scList.map((s, i) => (
              <Area key={s.key} type="monotone" dataKey={s.key} stroke={s.color} fill={`url(#g${i})`}
                strokeWidth={2.5} strokeDasharray={i > 0 ? '8 4' : undefined}
                dot={{ r: 3, strokeWidth: 2, fill: '#fff' }} connectNulls />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      );
    }
    if (chartType === 'bars') {
      return (
        <ResponsiveContainer width="100%" height={h}>
          <ComposedChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#64748b' }} angle={-30} textAnchor="end" height={50} />
            <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
            <Tooltip content={<CustomTooltip fmt={fmt} />} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {scList.map((s) => <Bar key={s.key} dataKey={s.key} fill={s.color} radius={[4, 4, 0, 0]} opacity={0.85} />)}
          </ComposedChart>
        </ResponsiveContainer>
      );
    }
    if (chartType === 'radar') {
      const rd = monthRange.map((m, i) => {
        const d = { month: m.label, 'Scenario prudent': currentData.values[i] || 0 };
        if (realisticTarget > 0) d['Scenario realiste'] = realisticData[i];
        if (growthTarget > 0) d['Scenario de developpement'] = growthData[i];
        return d;
      });
      return (
        <ResponsiveContainer width="100%" height={h}>
          <RadarChart cx="50%" cy="50%" outerRadius="75%" data={rd}>
            <PolarGrid stroke="#e2e8f0" />
            <PolarAngleAxis dataKey="month" tick={{ fontSize: 9, fill: '#64748b' }} />
            <PolarRadiusAxis tick={{ fontSize: 9, fill: '#94a3b8' }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
            <Tooltip content={<CustomTooltip fmt={fmt} />} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {scList.map(s => <Radar key={s.key} name={s.key} dataKey={s.key} stroke={s.color} fill={s.color} fillOpacity={0.12} strokeWidth={2} dot={{ r: 3 }} />)}
          </RadarChart>
        </ResponsiveContainer>
      );
    }
  };

  const bgClasses = ['from-blue-50 to-blue-100/50 border-blue-200', 'from-amber-50 to-amber-100/50 border-amber-200', 'from-emerald-50 to-emerald-100/50 border-emerald-200'];
  const icons = [BarChart3, Target, Rocket];

  return (
    <div className="space-y-6" data-testid="projections-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex-1">
          {editingTitle ? (
            <div className="flex items-center gap-2">
              <Input value={title} onChange={e => setTitle(e.target.value)} className="text-2xl font-bold h-10 max-w-md" autoFocus onKeyDown={e => e.key === 'Enter' && setEditingTitle(false)} />
              <Button variant="ghost" size="sm" onClick={() => setEditingTitle(false)}><Check className="w-4 h-4" /></Button>
            </div>
          ) : (
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 cursor-pointer flex items-center gap-2" onClick={() => setEditingTitle(true)}>
              {title} <Edit3 className="w-4 h-4 text-slate-400" />
            </h1>
          )}
          <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="Description / contexte..." className="mt-1 h-7 text-sm max-w-xl" />
        </div>
        <Button onClick={handlePrint} className="bg-blue-600 hover:bg-blue-700" data-testid="print-projection-btn">
          <Printer className="w-4 h-4 mr-2" />Imprimer
        </Button>
      </div>

      {/* Period */}
      <Card className="border-blue-200 bg-blue-50/30">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <Label className="text-sm font-medium text-slate-700">De</Label>
            <select value={startMonth} onChange={e => setStartMonth(parseInt(e.target.value))} className="h-8 text-sm border border-slate-200 rounded-md px-2 bg-white" data-testid="start-month">
              {MONTHS_FULL.map((m, i) => <option key={i} value={i}>{m}</option>)}
            </select>
            <select value={startYear} onChange={e => setStartYear(parseInt(e.target.value))} className="h-8 text-sm border border-slate-200 rounded-md px-2 bg-white" data-testid="start-year">
              {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <Label className="text-sm font-medium text-slate-700">a</Label>
            <select value={endMonth} onChange={e => setEndMonth(parseInt(e.target.value))} className="h-8 text-sm border border-slate-200 rounded-md px-2 bg-white" data-testid="end-month">
              {MONTHS_FULL.map((m, i) => <option key={i} value={i}>{m}</option>)}
            </select>
            <select value={endYear} onChange={e => setEndYear(parseInt(e.target.value))} className="h-8 text-sm border border-slate-200 rounded-md px-2 bg-white" data-testid="end-year">
              {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <span className="text-xs text-blue-600 font-medium bg-blue-100 px-2 py-1 rounded">{periodMonths} mois</span>
          </div>
        </CardContent>
      </Card>

      {/* SITUATION ACTUELLE — manual entry */}
      <Card className={`bg-gradient-to-br ${bgClasses[0]} border hover:shadow-lg transition-all`}>
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-blue-100"><BarChart3 className="w-4 h-4 text-blue-600" /></div>
              <span className="text-sm font-bold text-slate-800">Scenario prudent — Saisie mensuelle</span>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-slate-500">Objectif annuel</Label>
              <Input type="number" value={realTarget || ''} onChange={e => setRealTarget(parseFloat(e.target.value) || 0)} placeholder="110000" className="w-32 h-8 text-sm bg-white/80" data-testid="real-target" />
            </div>
          </div>
          <p className="text-xs text-slate-500">Remplissez les mois passes. Les mois vides seront projetes automatiquement pour atteindre l'objectif.</p>
          <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-12 gap-2">
            {monthRange.map((m, i) => {
              const key = `${m.year}-${m.month}`;
              const val = realValues[key];
              const isReal = val !== undefined && val !== '' && val !== null;
              const projected = !isReal ? currentData.values[i] : null;
              return (
                <div key={i}>
                  <label className="text-[10px] text-slate-400 block text-center truncate">{m.label}</label>
                  <Input type="number" value={val ?? ''} onChange={e => setRealValues(prev => ({ ...prev, [key]: e.target.value }))}
                    placeholder={projected !== null ? String(projected) : '0'}
                    className={`h-8 text-xs text-center px-1 ${isReal ? 'bg-blue-100 border-blue-300 font-bold' : 'bg-white/60'}`} />
                </div>
              );
            })}
          </div>
          <div className="flex justify-between items-center">
            <p className="text-2xl font-extrabold text-blue-600">{fmt(totalCurrent)}</p>
            <p className="text-xs text-slate-500">Moy: {fmt(totalCurrent / Math.max(1, periodMonths))}/mois</p>
          </div>
          <div>
            <Label className="text-[10px] text-slate-500">Observation</Label>
            <Textarea value={realNote} onChange={e => setRealNote(e.target.value)} placeholder="Ex: Base sur les performances e-commerce actuelles..." rows={2} className="text-xs bg-white/80 resize-y" data-testid="note-current" />
          </div>
        </CardContent>
      </Card>

      {/* OBJECTIF REALISTE + DEVELOPPEMENT */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {[{ label: 'Scenario realiste', color: '#f59e0b', target: realisticTarget, setTarget: setRealisticTarget, note: realisticNote, setNote: setRealisticNote, total: totalRealistic, Icon: Target, bg: bgClasses[1], id: 'realistic', placeholder: 'Ex: Scenario 140k :\n- 10 clients/jour\n- Panier moyen 40 CHF\n- 10 x 40 x 30 = 12\'000/mois\n- ~144k/an' },
          { label: 'Scenario de developpement', color: '#10b981', target: growthTarget, setTarget: setGrowthTarget, note: growthNote, setNote: setGrowthNote, total: totalGrowth, Icon: Rocket, bg: bgClasses[2], id: 'growth', placeholder: 'Ex: Scenario 180k :\n- 15 clients/jour\n- Panier moyen 40 CHF\n- 15 x 40 x 30 = 18\'000/mois\n- ~216k potentiel' }
        ].map(s => (
          <Card key={s.id} className={`bg-gradient-to-br ${s.bg} border hover:shadow-lg transition-all`}>
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: s.color + '20' }}>
                  <s.Icon className="w-4 h-4" style={{ color: s.color }} />
                </div>
                <span className="text-sm font-bold text-slate-800">{s.label}</span>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Objectif annuel (CHF)</Label>
                <Input type="number" value={s.target || ''} onChange={e => s.setTarget(parseFloat(e.target.value) || 0)} placeholder="140000" className="h-9 text-lg font-bold bg-white/80" data-testid={`target-${s.id}`} />
              </div>
              <div className="flex justify-between items-center pt-1 border-t border-black/5">
                <div>
                  <p className="text-[10px] text-slate-500 mb-0.5">Reste pour atteindre l'objectif</p>
                  <p className="text-2xl font-extrabold" style={{ color: s.target > 0 && (s.target - realTotal) > 0 ? s.color : '#059669' }}>
                    {s.target > 0 ? (s.target - realTotal > 0 ? fmt(s.target - realTotal) : 'Objectif atteint') : '-'}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-500">~{fmt(s.target / 12)}/mois</p>
                  <p className="text-xs text-slate-500">Realise: {fmt(realTotal)}</p>
                  {s.target > 0 && realTotal > 0 && <p className="text-xs font-medium" style={{ color: s.color }}>{Math.round(realTotal / s.target * 100)}% atteint</p>}
                </div>
              </div>
              <div>
                <Label className="text-[10px] text-slate-500">Observation & hypotheses (visible sur le PDF)</Label>
                <Textarea value={s.note} onChange={e => s.setNote(e.target.value)} placeholder={s.placeholder}
                  rows={5} className="text-xs bg-white/80 resize-y" data-testid={`note-${s.id}`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Chart */}
      <div ref={chartRef}>
      <Card className="border-slate-200 overflow-hidden">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2"><Sparkles className="w-4 h-4 text-blue-500" />Projection sur {periodMonths} mois</CardTitle>
            <div className="flex gap-1 bg-slate-100 rounded-lg p-0.5">
              {CHART_TYPES.map(ct => (
                <button key={ct.id} onClick={() => setChartType(ct.id)}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${chartType === ct.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                  data-testid={`chart-type-${ct.id}`}>{ct.label}</button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-2">{renderChart()}</CardContent>
      </Card>
      </div>

      {/* Table */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2"><CardTitle className="text-base">Detail mensuel</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-200">
                <th className="text-left py-2 px-3 text-xs font-semibold text-slate-500">Mois</th>
                <th className="text-right py-2 px-3 text-xs font-semibold text-blue-600">Scenario prudent</th>
                <th className="text-right py-2 px-3 text-xs font-semibold text-amber-600">Scenario realiste</th>
                <th className="text-right py-2 px-3 text-xs font-semibold text-emerald-600">Scenario de developpement</th>
              </tr></thead>
              <tbody>
                {monthRange.map((m, i) => {
                  const isReal = i <= currentData.lastRealIdx;
                  return (
                    <tr key={i} className="border-b border-slate-50 hover:bg-slate-50">
                      <td className="py-1.5 px-3 text-xs font-medium text-slate-700">{m.label}</td>
                      <td className={`py-1.5 px-3 text-xs text-right ${isReal ? 'font-bold text-blue-700' : 'text-slate-400 italic'}`}>{fmt(currentData.values[i])}{!isReal && ' *'}</td>
                      <td className="py-1.5 px-3 text-xs text-right text-slate-600">{realisticTarget > 0 ? fmt(realisticData[i]) : '-'}</td>
                      <td className="py-1.5 px-3 text-xs text-right text-slate-600">{growthTarget > 0 ? fmt(growthData[i]) : '-'}</td>
                    </tr>
                  );
                })}
                <tr className="border-t-2 border-blue-200 bg-blue-50">
                  <td className="py-2 px-3 text-xs font-bold">Total</td>
                  <td className="py-2 px-3 text-xs text-right font-bold text-blue-600">{fmt(totalCurrent)}</td>
                  <td className="py-2 px-3 text-xs text-right font-bold text-amber-600">{fmt(totalRealistic)}</td>
                  <td className="py-2 px-3 text-xs text-right font-bold text-emerald-600">{fmt(totalGrowth)}</td>
                </tr>
              </tbody>
            </table>
            <p className="text-[10px] text-slate-400 mt-1">* Valeurs projetees (tendance automatique)</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProjectionsPage;
